import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSuiClient } from '@mysten/dapp-kit';
import {
  getPersonalStats,
  getProtocolStats,
  getLeaderboard,
  getUserRank,
  getUserPercentile,
  getDailyActivity,
  getCategoryBreakdown,
  getTotalBurned,
  getBurnPercentage,
  getWeeklyBurned,
  getMonthlyBurned,
  getGasStats,
  getGasComparison,
} from '../utils/statsManager';
import { getEmissionStats } from '../utils/emissionSchedule';
import {
  getLeaderboard as getLeaderboardFromDB,
  getProtocolTotals,
  getRecentActivity,
  getDailyActivityFromDB,
  getWalletRank,
  setUsername,
  getUsername,
} from '../utils/supabaseManager';
import { generateStatsTweet } from '../utils/shareUtils';
import ShareButton from './ShareButton';
import { BADGE_TIERS, getTierFromScore } from '../constants';
import './StatsPanel.css';

// ── SuiNS Reverse Lookup ────────────────────────────────────────────────────
// Resolves wallet address → .sui name via SuiNS reverse registry
// Free to read; no wallet interaction required
const SUINS_REVERSE_REGISTRY = '0x6e0ddefc0ad98889c04bab9639e512c21766c5e6d908b8d4f0bfbf353aa685';

async function resolveSuiNSName(suiClient, address) {
  if (!address) return null;
  try {
    const result = await suiClient.getDynamicFieldObject({
      parentId: SUINS_REVERSE_REGISTRY,
      name: { type: 'address', value: address },
    });
    const name = result?.data?.content?.fields?.value;
    return name ? `${name}.sui` : null;
  } catch {
    return null;
  }
}

function StatsPanel({ isOpen, onClose, currentUserPurity = 0, walletAddress = null }) {
  const suiClient = useSuiClient();
  const resolving = useRef(new Set());
  const [activeTab, setActiveTab] = useState('overview'); // overview, leaderboard, activity, tiers
  const [personalStats, setPersonalStats] = useState(null);
  const [protocolStats, setProtocolStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [userPercentile, setUserPercentile] = useState(null);
  
  // Burn statistics (Task 1)
  const [burnStats, setBurnStats] = useState({
    totalBurned: 0,
    burnPercentage: 0,
    weeklyBurned: 0,
    monthlyBurned: 0
  });
  
  // Emission statistics (Task 3)
  const [emissionStats, setEmissionStats] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadStats().catch(err => console.error('loadStats error:', err));
    }
  }, [isOpen, currentUserPurity, walletAddress]);

  const loadStats = async () => {
    const personal = getPersonalStats(walletAddress);
    setPersonalStats(personal);

    // Load leaderboard + protocol totals from Supabase
    const [leaders, protocolTotals, walletRankData] = await Promise.all([
      getLeaderboardFromDB(10),
      getProtocolTotals(),
      walletAddress ? getWalletRank(walletAddress) : Promise.resolve(null),
    ]);

    // Use Supabase protocol totals if available, fallback to local
    const protocol = protocolTotals
      ? {
          ...getProtocolStats(),
          totalPurifications: protocolTotals.totalPurifications,
          totalObjectsCleaned: protocolTotals.totalObjectsCleaned,
          totalPurityEarned: protocolTotals.totalPurityEarned,
          totalSuiReclaimed: protocolTotals.totalSuiReclaimed,
          activePurifiers: protocolTotals.activePurifiers,
        }
      : getProtocolStats();

    setProtocolStats(protocol);
    const finalLeaders = leaders.length > 0 ? leaders : getLeaderboard(10);
    setLeaderboard(finalLeaders);

    // Resolve SuiNS names in background — updates displayName for .sui holders
    // Priority: SuiNS name > custom username (already in displayName) > address
    const addresses = finalLeaders.map(l => l.address).filter(Boolean);
    resolveLeaderboardSuiNS(addresses, finalLeaders);

    // Use real rank from Supabase if available
    const rank = walletRankData ? walletRankData.rank : getUserRank(currentUserPurity);
    const percentile = walletRankData
      ? parseFloat(((walletRankData.total - walletRankData.rank + 1) / Math.max(walletRankData.total, 1) * 100).toFixed(1))
      : getUserPercentile(currentUserPurity);
    setUserRank(rank);
    setUserPercentile(percentile);
    
    // Load burn statistics (Task 1)
    setBurnStats({
      totalBurned: getTotalBurned(),
      burnPercentage: getBurnPercentage(),
      weeklyBurned: getWeeklyBurned(),
      monthlyBurned: getMonthlyBurned()
    });
    
    // Load emission statistics (Task 3)
    setEmissionStats(getEmissionStats());
  };

  // Resolve SuiNS names for leaderboard entries — non-blocking, updates in place
  const resolveLeaderboardSuiNS = async (addresses, currentLeaders) => {
    const unresolved = addresses.filter(addr => !resolving.current.has(addr));
    if (unresolved.length === 0) return;
    unresolved.forEach(addr => resolving.current.add(addr));

    await Promise.all(unresolved.map(async (addr) => {
      const suinsName = await resolveSuiNSName(suiClient, addr);
      resolving.current.delete(addr);
      if (suinsName) {
        setLeaderboard(prev => prev.map(entry =>
          entry.address === addr
            ? { ...entry, displayName: suinsName, hasSuiNS: true }
            : entry
        ));
      }
    }));
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="stats-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="stats-panel"
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 30, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="stats-header">
            <div className="stats-title">
              <span className="stats-icon">📊</span>
              <h2>Protocol Statistics</h2>
            </div>
            <button className="stats-close" onClick={onClose}>
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div className="stats-tabs">
            <button
              className={`stats-tab ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`stats-tab ${activeTab === 'leaderboard' ? 'active' : ''}`}
              onClick={() => setActiveTab('leaderboard')}
            >
              Leaderboard
            </button>
            <button
              className={`stats-tab ${activeTab === 'activity' ? 'active' : ''}`}
              onClick={() => setActiveTab('activity')}
            >
              Activity
            </button>
            <button
              className={`stats-tab ${activeTab === 'tiers' ? 'active' : ''}`}
              onClick={() => setActiveTab('tiers')}
            >
              🏆 Tiers
            </button>
          </div>

          {/* Content */}
          <div className="stats-content">
            {activeTab === 'overview' && (
              <OverviewTab
                personalStats={personalStats}
                protocolStats={protocolStats}
                userRank={userRank}
                userPercentile={userPercentile}
                burnStats={burnStats}
                emissionStats={emissionStats}
                walletAddress={walletAddress}
              />
            )}
            {activeTab === 'leaderboard' && (
              <LeaderboardTab leaderboard={leaderboard} currentUserPurity={currentUserPurity} walletAddress={walletAddress} />
            )}
            {activeTab === 'activity' && <ActivityTab />}
            {activeTab === 'tiers' && (
              <TiersTab 
                currentScore={currentUserPurity}
              />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════════

function OverviewTab({ personalStats, protocolStats, userRank, userPercentile, burnStats, emissionStats, walletAddress = null }) {
  if (!personalStats || !protocolStats) {
    return <div className="stats-loading">Loading statistics...</div>;
  }

  const categoryData = getCategoryBreakdown();
  const categoryTotal = categoryData.TOXIC + categoryData.SPAM + categoryData.DUST;

  return (
    <motion.div
      className="overview-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Personal Stats */}
      <section className="stats-section">
        <h3 className="section-title">YOUR FLEX DASHBOARD 💪</h3>
        <div className="stats-grid">
          <StatCard
            icon="⚡"
            label="NUKES DROPPED 💣"
            value={personalStats.totalPurifications.toLocaleString()}
            color="cyan"
          />
          <StatCard
            icon="🗑️"
            label="SCAMS DESTROYED 💀"
            value={personalStats.totalObjectsCleaned.toLocaleString()}
            color="purple"
          />
          <StatCard
            icon="✨"
            label="BAG SIZE 💰"
            value={personalStats.totalPurityEarned.toLocaleString()}
            color="blue"
          />
          <StatCard
            icon="💎"
            label="SUI Reclaimed"
            value={personalStats.totalSuiReclaimed === 0 ? '0' : personalStats.totalSuiReclaimed < 0.01 ? personalStats.totalSuiReclaimed.toFixed(6) : personalStats.totalSuiReclaimed < 1 ? personalStats.totalSuiReclaimed.toFixed(4) : personalStats.totalSuiReclaimed.toFixed(2)}
            suffix=" SUI"
            color="green"
          />
        </div>

        {/* Performance Metrics */}
        <div className="performance-metrics">
          <div className="metric-card">
            <div className="metric-icon">🏆</div>
            <div className="metric-content">
              <div className="metric-label">Global Rank</div>
              <div className="metric-value">#{userRank || 'N/A'}</div>
              <div className="metric-detail">
                Top {userPercentile || '0'}% of purifiers
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">🔥</div>
            <div className="metric-content">
              <div className="metric-label">Current Streak</div>
              <div className="metric-value">{personalStats.currentStreak} days</div>
              <div className="metric-detail">
                Longest: {personalStats.longestStreak} days
              </div>
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-icon">📈</div>
            <div className="metric-content">
              <div className="metric-label">Efficiency</div>
              <div className="metric-value">{personalStats.efficiency}</div>
              <div className="metric-detail">Objects per purification</div>
            </div>
          </div>
        </div>

        {/* 🐦 SOCIAL SHARING */}
        <div className="share-section">
          <ShareButton
            tweetText={generateStatsTweet({
              tier: BADGE_TIERS[getTierFromScore(personalStats.totalPurityEarned)]?.name || 'Novice',
              purityScore: personalStats.totalPurityEarned,
              purifications: personalStats.totalPurifications,
              distilEarned: personalStats.totalPurityEarned,
              percentile: userPercentile
            })}
            variant="gradient"
            size="medium"
            fullWidth={true}
            label="Share My Stats on 𝕏"
          />
        </div>

        {/* Category Breakdown */}
        {personalStats.totalObjectsCleaned > 0 && (
          <div className="category-breakdown">
            <h4>Your Cleanup Distribution</h4>
            <div className="category-bars">
              <CategoryBar
                label="Toxic"
                value={personalStats.categoryBreakdown.TOXIC}
                total={personalStats.totalObjectsCleaned}
                color="#ef4444"
                icon="🔴"
              />
              <CategoryBar
                label="Spam"
                value={personalStats.categoryBreakdown.SPAM}
                total={personalStats.totalObjectsCleaned}
                color="#eab308"
                icon="🟡"
              />
              <CategoryBar
                label="Dust"
                value={personalStats.categoryBreakdown.DUST}
                total={personalStats.totalObjectsCleaned}
                color="#94a3b8"
                icon="⚪"
              />
            </div>
          </div>
        )}
      </section>

              <GasOptimizationSection walletAddress={walletAddress} />

      {/* 🔥 DEFLATIONARY BURNS SECTION - TASK 1 */}
      <section className="stats-section burn-stats-section">
        <h3 className="section-title">🔥 DEFLATIONARY BURNS</h3>
        <div className="stats-grid">
          <StatCard
            icon="🔥"
            label="Total Burned Forever"
            value={burnStats.totalBurned.toLocaleString()}
            suffix=" $DISTIL"
            color="orange"
          />
          <StatCard
            icon="📉"
            label="% of Supply Burned"
            value={burnStats.burnPercentage.toFixed(4)}
            suffix="%"
            color="red"
          />
          <StatCard
            icon="📅"
            label="Burned This Week"
            value={burnStats.weeklyBurned.toLocaleString()}
            suffix=" $DISTIL"
            color="orange"
          />
          <StatCard
            icon="📆"
            label="Burned This Month"
            value={burnStats.monthlyBurned.toLocaleString()}
            suffix=" $DISTIL"
            color="red"
          />
        </div>
        
        <div className="burn-info-box">
          <div className="burn-info-icon">💡</div>
          <div className="burn-info-content">
            <strong>What are burns?</strong>
            <p>Every time someone purges objects, 1% of the $DISTIL reward is permanently removed from circulation, making your tokens more scarce and valuable!</p>
          </div>
        </div>
      </section>

      {/* 📉 EMISSION SCHEDULE SECTION - TASK 3 */}
      {emissionStats && (
        <section className="stats-section emission-stats-section">
          <h3 className="section-title">📉 SUPPLY-BASED HALVING</h3>
          
          <div className="current-phase-display">
            <div className="phase-badge">
              Phase {emissionStats.currentPhase.phase}
            </div>
            <div className="phase-info">
              <div className="phase-name">{emissionStats.currentPhase.name}</div>
              <div className="phase-rate">
                Current Rate: <span className="rate-value">{emissionStats.currentRate} $DISTIL</span> per purge
              </div>
            </div>
          </div>
          
          {!emissionStats.isComplete && emissionStats.nextPhase && (
            <div className="milestone-progress-box">
              <div className="milestone-header">
                <span className="milestone-label">
                  Progress to Phase {emissionStats.nextPhase.phase}: {emissionStats.nextPhase.name}
                </span>
                <span className="milestone-remaining">
                  {emissionStats.tokensToNext.toLocaleString()} tokens to go
                </span>
              </div>
              
              <div className="emission-progress-bar">
                <div 
                  className="emission-progress-fill" 
                  style={{ width: `${emissionStats.progressToNext}%` }}
                >
                  <span className="progress-percentage">
                    {emissionStats.progressToNext.toFixed(1)}%
                  </span>
                </div>
              </div>
              
              <div className="milestone-details-grid">
                <div className="milestone-detail">
                  <span className="detail-label">Emitted So Far:</span>
                  <span className="detail-value">{emissionStats.totalEmitted.toLocaleString()} $DISTIL</span>
                </div>
                <div className="milestone-detail">
                  <span className="detail-label">Next Rate:</span>
                  <span className="detail-value">{emissionStats.nextPhase.rate} $DISTIL</span>
                </div>
                {emissionStats.estimatedDays && (
                  <div className="milestone-detail">
                    <span className="detail-label">Est. Time:</span>
                    <span className="detail-value">~{emissionStats.estimatedDays} days</span>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {emissionStats.isComplete && (
            <div className="emission-complete-box">
              <span className="complete-icon">🎉</span>
              <span className="complete-text">All emission milestones completed!</span>
            </div>
          )}
          
          <div className="emission-info-box">
            <div className="emission-info-icon">💡</div>
            <div className="emission-info-content">
              <strong>What is supply-based halving?</strong>
              <p>Rewards decrease automatically as more $DISTIL is emitted. This creates fair, self-regulating tokenomics that reward early adopters while ensuring long-term sustainability!</p>
            </div>
          </div>
        </section>
      )}

      {/* Protocol Stats */}
      <section className="stats-section">
        <h3 className="section-title">GLOBAL DOMINATION STATS 🌍</h3>
        <div className="stats-grid">
          <StatCard
            icon="🌐"
            label="Total Purifications"
            value={protocolStats.totalPurifications.toLocaleString()}
            color="cyan"
          />
          <StatCard
            icon="👥"
            label="Active Purifiers"
            value={protocolStats.activePurifiers.toLocaleString()}
            color="purple"
          />
          <StatCard
            icon="🗑️"
            label="SCAMS DESTROYED 💀"
            value={protocolStats.totalObjectsCleaned.toLocaleString()}
            color="blue"
          />
          <StatCard
            icon="💰"
            label="SUI Reclaimed"
            value={protocolStats.totalSuiReclaimed === 0 ? '0' : protocolStats.totalSuiReclaimed < 0.01 ? protocolStats.totalSuiReclaimed.toFixed(6) : protocolStats.totalSuiReclaimed < 1 ? protocolStats.totalSuiReclaimed.toFixed(4) : protocolStats.totalSuiReclaimed.toFixed(2)}
            suffix=" SUI"
            color="green"
          />
        </div>

        {/* Protocol Category Pie Chart */}
        <div className="protocol-categories">
          <h4>Network Cleanup Distribution</h4>
          <div className="pie-chart-container">
            <PieChart data={categoryData} total={categoryTotal} />
          </div>
        </div>
      </section>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// LEADERBOARD TAB
// ═══════════════════════════════════════════════════════════════════════════

function LeaderboardTab({ leaderboard, currentUserPurity, walletAddress }) {
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [usernameInput, setUsernameInput] = useState('');
  const [usernameStatus, setUsernameStatus] = useState(null); // null | 'saving' | 'success' | 'error'
  const [usernameError, setUsernameError] = useState('');
  const [currentUsername, setCurrentUsername] = useState(null);

  useEffect(() => {
    if (walletAddress) {
      getUsername(walletAddress).then(u => setCurrentUsername(u));
    }
  }, [walletAddress]);

  const handleSetUsername = async () => {
    if (!usernameInput.trim() || !walletAddress) return;
    setUsernameStatus('saving');
    setUsernameError('');
    const result = await setUsername(walletAddress, usernameInput.trim());
    if (result.success) {
      setCurrentUsername(usernameInput.trim());
      setUsernameStatus('success');
      setTimeout(() => { setShowUsernameModal(false); setUsernameStatus(null); }, 1500);
    } else {
      setUsernameStatus('error');
      setUsernameError(result.error || 'Something went wrong');
    }
  };

  return (
    <motion.div
      className="leaderboard-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="leaderboard-header">
        <div className="leaderboard-title-row">
          <div>
            <h3>Top Purifiers</h3>
            <p className="leaderboard-subtitle">Leading the cleanup revolution</p>
          </div>
          {walletAddress && (
            <button
              className="set-username-btn"
              onClick={() => { setUsernameInput(currentUsername || ''); setShowUsernameModal(true); }}
            >
              {currentUsername ? `✏️ ${currentUsername}` : '+ Set Username'}
            </button>
          )}
        </div>
      </div>

      {/* Username Modal */}
      {showUsernameModal && (
        <motion.div
          className="username-modal"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="username-modal-inner">
            <div className="username-modal-title">
              {currentUsername ? 'Update Username' : 'Set Your Username'}
            </div>
            <p className="username-modal-hint">3–20 chars, letters/numbers/underscores</p>
            <input
              className="username-input"
              type="text"
              placeholder="e.g. sui_cleaner_9000"
              value={usernameInput}
              onChange={e => { setUsernameInput(e.target.value); setUsernameStatus(null); setUsernameError(''); }}
              maxLength={20}
              onKeyDown={e => e.key === 'Enter' && handleSetUsername()}
            />
            {usernameError && <div className="username-error">{usernameError}</div>}
            <div className="username-modal-actions">
              <button className="username-cancel-btn" onClick={() => setShowUsernameModal(false)}>
                Cancel
              </button>
              <button
                className="username-save-btn"
                onClick={handleSetUsername}
                disabled={usernameStatus === 'saving' || !usernameInput.trim()}
              >
                {usernameStatus === 'saving' ? 'Saving...' : usernameStatus === 'success' ? '✓ Saved!' : 'Save'}
              </button>
            </div>
          </div>
        </motion.div>
      )}

      <div className="leaderboard-list">
        {(!leaderboard || leaderboard.length === 0) ? (
          <div className="stats-loading">No leaderboard data yet - be the first purifier!</div>
        ) : leaderboard.map((purifier, index) => (
          <motion.div
            key={purifier.address}
            className={`leaderboard-item rank-${purifier.rank}`}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <div className="rank-badge">
              {purifier.rank <= 3 ? ['🥇', '🥈', '🥉'][purifier.rank - 1] : `#${purifier.rank}`}
            </div>

            <div className="purifier-info">
              <div className="purifier-name">
                <span className={purifier.hasSuiNS ? 'suins-display-name' : ''}>
                  {purifier.displayName}
                </span>
                {purifier.hasSuiNS && <span className="suins-tag">✦ .sui</span>}
                {!purifier.hasSuiNS && purifier.hasUsername && <span className="verified-tag">✓</span>}
              </div>
              {!purifier.hasUsername && !purifier.hasSuiNS && (
                <div className="purifier-address-sub">{purifier.address.slice(0,6)}...{purifier.address.slice(-4)}</div>
              )}
            </div>

            <div className="purifier-stats">
              <div className="stat-item">
                <span className="stat-label">Purity</span>
                <span className="stat-value">{purifier.totalPurity.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Purifications</span>
                <span className="stat-value">{purifier.purifications.toLocaleString()}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Objects</span>
                <span className="stat-value">{(purifier.objectsCleaned || 0).toLocaleString()}</span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ACTIVITY TAB
// ═══════════════════════════════════════════════════════════════════════════

function ActivityTab() {
  const [dailyActivity, setDailyActivity] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDailyActivityFromDB(30),
      getRecentActivity(20),
    ]).then(([daily, recent]) => {
      setDailyActivity(daily || []);
      setRecentActivity(recent || []);
      setLoading(false);
    }).catch(() => {
      setDailyActivity([]);
      setLoading(false);
    });
  }, []);

  const maxPurifications = dailyActivity.length > 0
    ? Math.max(...dailyActivity.map(d => d.purifications), 1)
    : 1;
  const hasActivity = dailyActivity.some(d => d.purifications > 0);

  return (
    <motion.div
      className="activity-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="activity-header">
        <h3>Daily Activity</h3>
        <p className="activity-subtitle">Network purifications over the last 30 days</p>
      </div>

      {!hasActivity ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <p>No activity data yet</p>
          <p className="empty-subtitle">Start purifying to see activity stats here!</p>
        </div>
      ) : (
        <>
          <div className="activity-chart">
            {dailyActivity.map((day, index) => {
              const height = (day.purifications / maxPurifications) * 100;
              const date = new Date(day.date);
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;

              return (
                <div key={day.date} className="activity-bar-container">
                  <motion.div
                    className={`activity-bar ${isWeekend ? 'weekend' : ''}`}
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ delay: index * 0.02, duration: 0.3 }}
                    title={`${day.date}: ${day.purifications} purifications`}
                  >
                    <div className="activity-tooltip">
                      <div className="tooltip-date">{day.date}</div>
                      <div className="tooltip-stat">{day.purifications} purifications</div>
                      <div className="tooltip-stat">{day.objectsCleaned.toLocaleString()} objects</div>
                      <div className="tooltip-stat">{day.purityEarned.toLocaleString()} purity</div>
                    </div>
                  </motion.div>
                  {index % 7 === 0 && (
                    <div className="activity-label">
                      {date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="activity-legend">
            <div className="legend-item">
              <div className="legend-color weekday"></div>
              <span>Weekday</span>
            </div>
            <div className="legend-item">
              <div className="legend-color weekend"></div>
              <span>Weekend</span>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UTILITY COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

function StatCard({ icon, label, value, suffix = '', color = 'cyan' }) {
  return (
    <motion.div
      className={`stat-card stat-card-${color}`}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
    >
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <div className="stat-label">{label}</div>
        <div className="stat-value">
          {value}
          {suffix && <span className="stat-suffix">{suffix}</span>}
        </div>
      </div>
    </motion.div>
  );
}

function CategoryBar({ label, value, total, color, icon }) {
  const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;

  return (
    <div className="category-bar-item">
      <div className="category-bar-header">
        <span className="category-icon">{icon}</span>
        <span className="category-label">{label}</span>
        <span className="category-value">
          {value.toLocaleString()} ({percentage}%)
        </span>
      </div>
      <div className="category-bar-track">
        <motion.div
          className="category-bar-fill"
          style={{ backgroundColor: color }}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        />
      </div>
    </div>
  );
}

function PieChart({ data, total }) {
  const toxicPercent = (data.TOXIC / total) * 100;
  const spamPercent = (data.SPAM / total) * 100;
  const dustPercent = (data.DUST / total) * 100;

  return (
    <div className="pie-chart">
      <svg viewBox="0 0 200 200" className="pie-svg">
        <circle cx="100" cy="100" r="90" fill="none" stroke="#1e293b" strokeWidth="20" />
        
        {/* Toxic segment */}
        <circle
          cx="100"
          cy="100"
          r="90"
          fill="none"
          stroke="#ef4444"
          strokeWidth="20"
          strokeDasharray={`${toxicPercent * 5.65} 565`}
          strokeDashoffset="0"
          transform="rotate(-90 100 100)"
        />
        
        {/* Spam segment */}
        <circle
          cx="100"
          cy="100"
          r="90"
          fill="none"
          stroke="#eab308"
          strokeWidth="20"
          strokeDasharray={`${spamPercent * 5.65} 565`}
          strokeDashoffset={`-${toxicPercent * 5.65}`}
          transform="rotate(-90 100 100)"
        />
        
        {/* Dust segment */}
        <circle
          cx="100"
          cy="100"
          r="90"
          fill="none"
          stroke="#94a3b8"
          strokeWidth="20"
          strokeDasharray={`${dustPercent * 5.65} 565`}
          strokeDashoffset={`-${(toxicPercent + spamPercent) * 5.65}`}
          transform="rotate(-90 100 100)"
        />
      </svg>

      <div className="pie-legend">
        <div className="pie-legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#ef4444' }}></span>
          <span>Toxic: {toxicPercent.toFixed(1)}%</span>
        </div>
        <div className="pie-legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#eab308' }}></span>
          <span>Spam: {spamPercent.toFixed(1)}%</span>
        </div>
        <div className="pie-legend-item">
          <span className="legend-dot" style={{ backgroundColor: '#94a3b8' }}></span>
          <span>Dust: {dustPercent.toFixed(1)}%</span>
        </div>
      </div>
    </div>
  );
}

export default StatsPanel;

// ═══════════════════════════════════════════════════════════════════════════
// TIERS TAB - Shows all 8 badge tiers
// ═══════════════════════════════════════════════════════════════════════════

function TiersTab({ currentScore }) {
  // Derive current tier directly from score - single source of truth
  const currentTier = getTierFromScore(currentScore || 0);

  // Derived directly from BADGE_TIERS in constants.js - single source of truth
  // 8-tier system: more frequent milestones = better user engagement
  const tiers = [
    {
      level: 0,
      name: 'Novice',
      icon: '🌱',
      threshold: 0,
      nextThreshold: 50,
      description: 'Just getting started on your purification journey',
      benefits: ['Basic purification access', 'Start earning purity points'],
      color: '#9ca3af',
    },
    {
      level: 1,
      name: 'Apprentice',
      icon: '⚙️',
      threshold: 50,
      nextThreshold: 200,
      description: 'Learning the ropes of blockchain purification',
      benefits: ['Community visibility', 'Faster progression'],
      color: '#60a5fa',
    },
    {
      level: 2,
      name: 'Adept',
      icon: '⚡',
      threshold: 200,
      nextThreshold: 500,
      description: 'Getting the hang of identifying toxic assets',
      benefits: ['Increased reputation', 'Access to advanced filters'],
      color: '#8b5cf6',
    },
    {
      level: 3,
      name: 'Expert',
      icon: '💎',
      threshold: 500,
      nextThreshold: 1000,
      description: 'Purification master - filth doesn\'t stand a chance',
      benefits: ['Community recognition', 'Leaderboard tracking'],
      color: '#22d3ee',
    },
    {
      level: 4,
      name: 'Master',
      icon: '👑',
      threshold: 1000,
      nextThreshold: 2500,
      description: 'Elite purifier with proven track record',
      benefits: ['Enhanced rewards', 'Priority support'],
      color: '#a855f7',
    },
    {
      level: 5,
      name: 'Elite',
      icon: '🔥',
      threshold: 2500,
      nextThreshold: 5000,
      description: 'Top tier performance - one of the best on Sui',
      benefits: ['Bonus multipliers', 'VIP treatment'],
      color: '#f59e0b',
    },
    {
      level: 6,
      name: 'Legendary',
      icon: '⚔️',
      threshold: 5000,
      nextThreshold: 10000,
      description: 'Legendary status achieved - the ecosystem knows your name',
      benefits: ['Maximum rewards', 'Legendary badge', 'Protocol influence'],
      color: '#ef4444',
    },
    {
      level: 7,
      name: 'Mythic',
      icon: '🌟',
      threshold: 10000,
      nextThreshold: null,
      description: 'The pinnacle of purification mastery - absolute legend',
      benefits: ['All benefits unlocked', 'Revenue share eligibility', 'Eternal glory', 'Mythic badge'],
      color: '#FFD700',
    },
  ];

  return (
    <motion.div
      className="tiers-tab"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="tiers-header">
        <h3>🏆 Badge Tier System</h3>
        <p>Level up by earning purity points through purifications</p>
      </div>

      <div className="tiers-list">
        {tiers.map((tier, index) => {
          const isCompleted = currentTier > tier.level;
          const isCurrent = currentTier === tier.level;
          const isLocked = currentTier < tier.level;
          
          let progress = 0;
          if (isCurrent && tier.nextThreshold) {
            progress = ((currentScore - tier.threshold) / (tier.nextThreshold - tier.threshold)) * 100;
            progress = Math.max(0, Math.min(100, progress));
          } else if (isCurrent && !tier.nextThreshold) {
            progress = 100; // Max tier
          }

          return (
            <motion.div
              key={tier.level}
              className={`tier-card ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''} ${isLocked ? 'locked' : ''}`}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <div className="tier-header-card">
                <div className="tier-icon">{tier.icon}</div>
                <div className="tier-info">
                  <div className="tier-title">
                    <span className="tier-name">{tier.name}</span>
                    {isCurrent && <span className="tier-badge-current">YOU ARE HERE</span>}
                    {isCompleted && <span className="tier-badge-completed">✓ UNLOCKED</span>}
                    {isLocked && <span className="tier-badge-locked">🔒 LOCKED</span>}
                  </div>
                  <div className="tier-threshold">
                    {tier.threshold.toLocaleString()} points
                    {index < tiers.length - 1 && ` - ${tiers[index + 1].threshold.toLocaleString()} points`}
                  </div>
                </div>
              </div>

              <div className="tier-description">{tier.description}</div>

              {isCurrent && (
                <div className="tier-progress">
                  <div className="tier-progress-bar">
                    <div 
                      className="tier-progress-fill" 
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="tier-progress-text">
                    {(currentScore || 0).toLocaleString()} / {tier.nextThreshold ? tier.nextThreshold.toLocaleString() : '∞'} 
                    <span className="tier-progress-percent">({progress.toFixed(0)}%)</span>
                  </div>
                </div>
              )}

              <div className="tier-benefits">
                <strong>Benefits:</strong>
                <ul>
                  {tier.benefits.map((benefit, i) => (
                    <li key={i}>{benefit}</li>
                  ))}
                </ul>
              </div>
            </motion.div>
          );
        })}
      </div>
    </motion.div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════
// GAS OPTIMIZATION SECTION COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

function GasOptimizationSection({ walletAddress = null }) {
  const gasStats = getGasStats(walletAddress);
  const comparison = getGasComparison();
  
  if (gasStats.totalObjectsCleaned === 0) {
    return (
      <section className="stats-section gas-section">
        <h3 className="section-title">⛽ GAS OPTIMIZATION</h3>
        <div className="gas-empty">
          <div className="empty-icon">⛽</div>
          <p>Start purifying to see your gas savings!</p>
          <p className="empty-subtitle">
            Batch deletions save you tons of gas compared to manual one-by-one deletions
          </p>
        </div>
      </section>
    );
  }
  
  return (
    <section className="stats-section gas-section">
      <h3 className="section-title">⛽ GAS OPTIMIZATION - YOUR SAVINGS 💰</h3>
      
      {/* Main Savings Highlight */}
      <div className="gas-highlight">
        <div className="gas-hero-stat">
          <div className="gas-hero-label">TOTAL GAS SAVED</div>
          <div className="gas-hero-value">
            {gasStats.totalGasSaved} <span className="gas-unit">SUI</span>
          </div>
          <div className="gas-hero-usd">
            ≈ ${gasStats.gasSavedInUSD} USD
          </div>
          <div className="gas-hero-percent">
            {gasStats.gasSavingsPercentage}% more efficient than manual! 🎯
          </div>
        </div>
      </div>
      
      {/* Comparison Bars */}
      <div className="gas-comparison">
        <h4 className="gas-comparison-title">Gas Usage Comparison</h4>
        <div className="gas-bars">
          {/* Distil (Your Method) */}
          <div className="gas-bar-row">
            <div className="gas-bar-label">
              <span className="gas-bar-icon">⚡</span>
              <span>With Distil (Batch)</span>
            </div>
            <div className="gas-bar-container">
              <div 
                className="gas-bar gas-bar-good"
                style={{
                  width: `${(comparison.withDistil.value / comparison.withoutDistil.value) * 100}%`
                }}
              >
                <span className="gas-bar-value">{comparison.withDistil.value} SUI</span>
              </div>
            </div>
          </div>
          
          {/* Manual (What they'd use) */}
          <div className="gas-bar-row">
            <div className="gas-bar-label">
              <span className="gas-bar-icon">🐌</span>
              <span>Manual (One-by-One)</span>
            </div>
            <div className="gas-bar-container">
              <div 
                className="gas-bar gas-bar-bad"
                style={{ width: '100%' }}
              >
                <span className="gas-bar-value">{comparison.withoutDistil.value} SUI</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="gas-comparison-footer">
          You saved <strong>{gasStats.totalGasSaved} SUI</strong> by using batch deletions! 🚀
        </div>
      </div>
      
      {/* Efficiency Metrics Grid */}
      <div className="gas-metrics-grid">
        <div className="gas-metric-card">
          <div className="gas-metric-icon">📊</div>
          <div className="gas-metric-value">{gasStats.averageGasPerPurification}</div>
          <div className="gas-metric-label">Avg Gas / Purification</div>
        </div>
        
        <div className="gas-metric-card">
          <div className="gas-metric-icon">🎯</div>
          <div className="gas-metric-value">{gasStats.efficiencyScore}</div>
          <div className="gas-metric-label">Objects / SUI Gas</div>
        </div>
        
        <div className="gas-metric-card">
          <div className="gas-metric-icon">💎</div>
          <div className="gas-metric-value">{gasStats.totalObjectsCleaned}</div>
          <div className="gas-metric-label">Total Objects Cleaned</div>
        </div>
        
        <div className="gas-metric-card">
          <div className="gas-metric-icon">⛽</div>
          <div className="gas-metric-value">{gasStats.totalGasUsed}</div>
          <div className="gas-metric-label">Total Gas Used (SUI)</div>
        </div>
      </div>
      
      {/* Value Proposition */}
      <div className="gas-value-prop">
        <div className="value-prop-header">
          <span className="value-prop-icon">💡</span>
          <span className="value-prop-title">Why Distil Saves You Money</span>
        </div>
        <div className="value-prop-content">
          <div className="value-prop-point">
            <span className="value-prop-bullet">✓</span>
            <span>Batch multiple deletions into one transaction</span>
          </div>
          <div className="value-prop-point">
            <span className="value-prop-bullet">✓</span>
            <span>Pay gas once instead of for each object</span>
          </div>
          <div className="value-prop-point">
            <span className="value-prop-bullet">✓</span>
            <span>Get back 92% of storage deposits</span>
          </div>
        </div>
      </div>
    </section>
  );
}
